import { useEffect, useState } from 'react'
import { Box, Col, Grid, Heading, ProgressBar, Table, Td, Text, TextLink, Th } from '@ui'
import { getJSON } from '../api.js'
import { addDays, dateKey, todayKey, utcDate } from '../dates.js'
import { DAY_STEPS_GOAL, WEEK_BRISK_GOAL, dayFigures, firstBriskWeek, weekOf, weekSummary } from '../goals.js'

const RECENT_WEEKS = 12

const dayName = (key) => utcDate(key).toLocaleDateString('en-GB', { timeZone: 'UTC', weekday: 'short', day: 'numeric', month: 'short' })
const weekName = (monday) => utcDate(monday).toLocaleDateString('en-GB', { timeZone: 'UTC', day: 'numeric', month: 'short', year: 'numeric' })
const n = (v) => Math.round(v).toLocaleString('en-GB')

// "45" or "45*" when a walk that day (or week) had no brisk minutes to add.
function briskText(value, missing) {
  if (value == null) return missing ? '–' : ''
  return `${n(value)}${missing ? '*' : ''}`
}

function briskLine(week) {
  const left = WEEK_BRISK_GOAL - week.brisk
  return left > 0 ? `${n(left)} to go` : 'Reached'
}

const FOOTNOTE = `From walks only, each walk once (not all-day steps, as Google Fit counts). Brisk minutes are
  clock minutes with at least 100 steps; they start on 27 Oct 2026, the first walks with the app's step
  readings. * a walk without brisk minutes (MapMyWalk only, or before then).`

// Targets page section: this week against the goals, its days, and recent weeks.
export function GoalsSection({ walks }) {
  const days = dayFigures(walks)
  const today = todayKey()
  const thisWeek = weekSummary(days, weekOf(today))
  const daysLeft = 6 - Math.round((utcDate(today) - utcDate(thisWeek.monday)) / 86400000)
  const first = firstBriskWeek(days)
  const weeks = []
  if (first) {
    for (let m = thisWeek.monday; m >= first && weeks.length < RECENT_WEEKS; m = dateKey(addDays(utcDate(m), -7))) {
      weeks.push(weekSummary(days, m))
    }
  }

  return (
    <>
      <Heading level={2} size={5} mt={4} id="brisk">Brisk minutes and steps</Heading>
      <Grid mb={2}>
        <Col xs={12} md={6} mb={3}>
          <Box muted text="small">This week</Box>
          <Box text="xl" weight="semibold" tight>
            {briskText(thisWeek.brisk, thisWeek.briskMissing) || '0'} of {WEEK_BRISK_GOAL} brisk minutes
          </Box>
          <ProgressBar value={Math.min(100, (thisWeek.brisk / WEEK_BRISK_GOAL) * 100)} />
          <Box text="small" muted mt={1}>
            {briskLine(thisWeek)}{thisWeek.brisk < WEEK_BRISK_GOAL && `, ${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} left after today`}
          </Box>
        </Col>
        <Col xs={12} md={6} mb={3}>
          <Box muted text="small">Days with {n(DAY_STEPS_GOAL)} steps this week</Box>
          <Box text="xl" weight="semibold" tight>{thisWeek.stepDays} of 7</Box>
          <Box text="small" muted>{thisWeek.walks} {thisWeek.walks === 1 ? 'walk' : 'walks'} so far</Box>
        </Col>
      </Grid>

      <Table autoWidth>
        <thead>
          <tr><th>Day</th><Th numeric>Brisk minutes</Th><Th numeric>Steps</Th></tr>
        </thead>
        <tbody>
          {thisWeek.days.map((d) => (
            <tr key={d.key}>
              <td>{dayName(d.key)}{d.key === today && <Text muted> (today)</Text>}</td>
              <Td numeric>{briskText(d.brisk, d.briskMissing)}</Td>
              <Td numeric>{d.walks ? n(d.steps) : ''}{d.steps >= DAY_STEPS_GOAL && ' ✓'}</Td>
            </tr>
          ))}
        </tbody>
      </Table>

      {weeks.length > 1 && (
        <>
          <Heading level={3} size={6} mt={3}>Recent weeks</Heading>
          <Table autoWidth>
            <thead>
              <tr><th>Week of</th><Th numeric>Brisk minutes</Th><th /><Th numeric>Days with {n(DAY_STEPS_GOAL)} steps</Th></tr>
            </thead>
            <tbody>
              {weeks.map((w) => (
                <tr key={w.monday}>
                  <td>{weekName(w.monday)}</td>
                  <Td numeric>{briskText(w.brisk, w.briskMissing) || '0'}</Td>
                  <td>{w.brisk >= WEEK_BRISK_GOAL ? 'Reached' : w.monday === thisWeek.monday ? '' : 'Missed'}</td>
                  <Td numeric>{w.stepDays}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </>
      )}
      <Text as="p" muted text="small">{FOOTNOTE}</Text>
    </>
  )
}

// Home page card: this week's brisk minutes and step days, linking to the Targets section.
export function ThisWeek() {
  const [walks, setWalks] = useState(null)

  useEffect(() => {
    const controller = new AbortController()
    getJSON('/api/walks', { signal: controller.signal })
      .then(setWalks)
      .catch(() => {}) // the card just doesn't show
    return () => controller.abort()
  }, [])

  if (!walks) return null
  const week = weekSummary(dayFigures(walks), weekOf(todayKey()))
  return (
    <>
      <Heading level={2} size={5} mt={4}>This week</Heading>
      <Box mb={1}>
        {briskText(week.brisk, week.briskMissing) || '0'} of {WEEK_BRISK_GOAL} brisk minutes ({briskLine(week).toLowerCase()})
        {' · '}{week.stepDays} {week.stepDays === 1 ? 'day' : 'days'} with {n(DAY_STEPS_GOAL)} steps
      </Box>
      <Box className="week-progress">
        <ProgressBar value={Math.min(100, (week.brisk / WEEK_BRISK_GOAL) * 100)} />
      </Box>
      <Text as="p" muted text="small" mt={1}><TextLink to="/targets#brisk">Weeks and days on Targets</TextLink></Text>
    </>
  )
}
